import DatePicker from "react-datepicker";
import { MdDeleteForever } from "react-icons/md";
import LocationAutoComplete from "../../../AutoComplete/AutoComplete";
import styles from "./FileReviewScreen.module.css";
import { useMemo, useState } from "react";
import TruckSelector from "../TruckSelector/TruckSelector";

export default function FileReviewScreen({
  lanes,
  setLanes,
  onSubmit,
  onBack,
}) {
  const [showIssues, setShowIssues] = useState(false);

  const issues = useMemo(() => {
    return lanes
      .map((lane, idx) => {
        const missing = [];
        if (!lane.origin_details) missing.push("Origin");
        if (!lane.destination_details) missing.push("Destination");
        if (!lane.truck_type?.trim()) missing.push("Truck Type");
        if (!lane.date) missing.push("Date");

        const dims = lane.truck_details?.user_dimensions || {};
        const dimsMissing = [];
        if (!dims.capacity_mt) dimsMissing.push("Capacity (MT)");
        if (!dims.length_ft) dimsMissing.push("Length (FT)");
        if (!dims.axle_type) dimsMissing.push("Axle Type");
        if (!dims.body_type) dimsMissing.push("Body Type");

        if (dimsMissing.length)
          missing.push(`Dimensions: ${dimsMissing.join(", ")}`);

        return missing.length ? { index: idx, missing } : null;
      })
      .filter(Boolean);
  }, [lanes]);

  const issuesCount = issues.length;

  const scrollToLane = (i) => {
    const el = document.getElementById(`lane-row-${i}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add(styles.rowHighlight || "");
      setTimeout(() => el.classList.remove(styles.rowHighlight || ""), 1500);
    }
  };

  const handleLaneChange = (index, field, value) => {
    const updated = [...lanes];

    if (value?.full) {
      if (field === "origin") updated[index].origin_details = value.full;
      if (field === "destination")
        updated[index].destination_details = value.full;
    } else {
      // Fallback for clear / null case
      updated[index][field] = value;
    }

    setLanes(updated);
  };

  const handleClearField = (index, field) => {
    const updated = [...lanes];
    if (field === "origin") {
      updated[index].origin_details = null;
    }
    if (field === "destination") {
      updated[index].destination_details = null;
    }
    setLanes(updated);
  };

  const handleRemoveLane = (index) => {
    const updated = lanes?.filter((_, i) => i !== index);
    setLanes(updated);
  };

  const allValid = useMemo(
    () =>
      lanes.every(
        ({
          origin_details,
          destination_details,
          truck_details,
          truck_type,
          date,
        }) => {
          const dims = truck_details?.user_dimensions || {};
          const dimsComplete =
            dims.capacity_mt &&
            dims.length_ft &&
            dims.axle_type &&
            dims.body_type;
          return (
            origin_details &&
            destination_details &&
            truck_type?.trim() &&
            date &&
            dimsComplete
          );
        }
      ),
    [lanes]
  );

  return (
    <div className={styles.reviewContainer}>
      <div className={styles.reviewContent}>
        <div className={styles.headerRow}>
          <div>
            <h3 className={styles.heading}>Review Uploaded Lanes</h3>
            <p className={styles.subheading}>
              Please verify the data before generating the forecast.
            </p>
          </div>

          {issuesCount > 0 && (
            <div className={styles.issuesWrapper}>
              <button
                type="button"
                onClick={() => issuesCount && setShowIssues((s) => !s)}
                title={issuesCount ? "Show issues" : "No issues"}
                className={`${styles.issuesButton} ${
                  issuesCount ? styles.issuesButtonError : styles.issuesButtonOk
                }`}
              >
                Issues
                <span
                  className={`${styles.issuesCount} ${
                    issuesCount ? styles.issuesCountError : styles.issuesCountOk
                  }`}
                >
                  {issuesCount}
                </span>
              </button>

              {showIssues && issuesCount > 0 && (
                <div className={styles.issuesPopover}>
                  <div className={styles.issuesTitle}>Lanes to fix</div>
                  <div className={styles.issueContainer}>
                    <ul className={styles.issuesList}>
                      {issues.map(({ index, missing }) => (
                        <li key={index} className={styles.issuesItem}>
                          <div className={styles.issuesItemText}>
                            <div className={styles.issuesLane}>
                              Lane #{index + 1}
                            </div>
                            <div className={styles.issuesMissing}>
                              {missing.join("; ")}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              scrollToLane(index);
                              setShowIssues(false);
                            }}
                            className={styles.issuesJumpBtn}
                          >
                            Jump
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <table className={styles.reviewTable}>
          <colgroup>
            <col className={styles.colSrNo} />
            <col className={styles.colOrigin} />
            <col className={styles.colDestination} />
            <col className={styles.colTruck} />
            <col className={styles.colDate} />
            <col className={styles.colActions} />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Origin</th>
              <th>Destination</th>
              <th>Truck Type</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lanes.map((lane, index) => {
              const originInvalid = !lane.origin_details;
              const destInvalid = !lane.destination_details;
              const truckInvalid = !lane.truck_details;
              const dateInvalid = !lane.date;

              return (
                <tr key={index} id={`lane-row-${index}`}>
                  <td className="text-center text-muted">{index + 1}.</td>
                  <td>
                    <LocationAutoComplete
                      value={
                        lane?.origin_details
                          ? {
                              value: lane?.origin_details?.location,
                              label: lane?.origin_details?.location_name || "",
                            }
                          : {
                              value: lane?.origin || "",
                              label: lane?.origin || "",
                            }
                      }
                      onChange={(val) => handleLaneChange(index, "origin", val)}
                      onClear={() => handleClearField(index, "origin")}
                      placeholder="Origin"
                      className={
                        originInvalid ? styles.errorInput : styles.inputWrapper
                      }
                      fontSize={12}
                      menuHeight={200}
                      // width={150}
                    />
                  </td>
                  <td>
                    <LocationAutoComplete
                      value={
                        lane?.destination_details
                          ? {
                              value: lane?.destination_details?.location,
                              label:
                                lane?.destination_details?.location_name || "",
                            }
                          : {
                              value: lane?.destination || "",
                              label: lane?.destination || "",
                            }
                      }
                      onChange={(val) =>
                        handleLaneChange(index, "destination", val)
                      }
                      onClear={() => handleClearField(index, "destination")}
                      placeholder="Destination"
                      className={
                        destInvalid ? styles.errorInput : styles.inputWrapper
                      }
                      fontSize={12}
                      menuHeight={200}
                      // width={150}
                    />
                  </td>
                  <td>
                    <TruckSelector
                      truckType={lane?.truck_details || {}}
                      setTruckType={(newTruck) => {
                        const updated = [...lanes];
                        updated[index].truck_type =
                          newTruck.raw_truck_name || "";
                        updated[index].truck_details = newTruck;
                        setLanes(updated);
                      }}
                      fontSize="12px"
                      menuHeight={200}
                      width={"100%"}
                      inlineDimensions={true}
                    />
                  </td>
                  <td>
                    <DatePicker
                      selected={lane.date ? new Date(lane.date) : null}
                      onChange={(date) => handleLaneChange(index, "date", date)}
                      className={
                        dateInvalid ? styles.errorInput : styles.textInput
                      }
                      placeholderText="Select Date"
                      dateFormat="dd-MM-yyyy"
                      minDate={new Date()}
                      maxDate={
                        new Date(
                          new Date().setMonth(new Date().getMonth() + 11)
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className={styles.removeBtn}
                      onClick={() => handleRemoveLane(index)}
                    >
                      <MdDeleteForever />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.buttonRow}>
        <button className={styles.cancelBtn} onClick={onBack}>
          Back
        </button>
        <button
          className={styles.submitBtn}
          onClick={onSubmit}
          disabled={!allValid || lanes.length < 1}
        >
          Generate Forecast
        </button>
      </div>
    </div>
  );
}
